import { Button } from '@codegouvfr/react-dsfr/Button';
import { useTextes } from "@common/config";
import { useCurrentCampagne} from "@common/hooks/campagne";
import { CampagneProjection, useGetCampagneByAnneeQuery, useGetOrganismeForCampagneQuery } from "@common/services";
import { Form, NumberField, SelectField, SelectOption } from '@common/ui';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/store.hook';
import { useInscriptionState } from '../../../hooks/use-inscription-state.hook';
import { patchOrganismeInfo } from '../../../stores/inscription.slice';
import { StepHeader } from '../components/StepHeader';
import { useStepperInfo } from '../hooks/use-stepper-info.hook';
import { additionalInfoSchema } from './schemas/additionalInfo.schema';
import { AdditionalInfoFormData } from './types/additionalInfo.type';

export const Step3 = () => {
  const t = useTextes('Inscription', 'Etape3InfoComplémentaires');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const inscription = useInscriptionState();

  useStepperInfo({ step: 3 });

  const { campagne } = useCurrentCampagne();

  const { data: campagnePrecedente } = useGetCampagneByAnneeQuery(
      { year: (campagne?.annee ?? 0) - 1, projection: CampagneProjection.ANNEE },
      { skip: !campagne?.annee },
  );

  const { data: organismePrecedent, isLoading: isLoadingOrganismePrecedent } = useGetOrganismeForCampagneQuery(
      { siren: inscription.organisme?.siren, campagneId: campagnePrecedente?.id },
      { skip: !campagnePrecedente?.id || !inscription.organisme?.siren },
  );

  const isReinscription = !!campagnePrecedente && !!organismePrecedent;

  const handleSubmit = (data: AdditionalInfoFormData) => {
    const { apprentiNumber, nda } = data;
    dispatch(patchOrganismeInfo({ effectifApprenti: apprentiNumber, nda }));
    navigate('/recapitulatif');
  };

  const getDefaultNda = (): string | undefined => {
    if (isReinscription) {
      const previousNda = organismePrecedent?.nda;

      if (inscription.ndas.length > 1) {
        return inscription.ndas.find((nda) => nda === previousNda);
      }

      if (inscription.ndas.length === 1) {
        return inscription.ndas[0];
      }

      return undefined;
    }

    return inscription.ndas.length ? inscription.ndas[0] : inscription.organisme?.nda;
  };

  const defaultValues = {
    apprentiNumber: inscription.organisme?.effectifApprenti > 0 ? inscription.organisme?.effectifApprenti : null,
    nda: getDefaultNda(),
  };

  const ndaOptions: SelectOption[] =
      isReinscription && inscription.ndas.length === 0
        ? []
        : inscription.ndas.map((value) => ({ value, label: value }));


  if (isLoadingOrganismePrecedent) return null;

  return (
    <div className={'flex flex-col gap-4'}>

      <StepHeader title={'Informations complémentaires'} text={t['INSCR_COMP_TEXT4']} />

      <Form<AdditionalInfoFormData>
        schema={additionalInfoSchema}
        onSubmit={handleSubmit}
        className={'flex flex-col gap-4 pt-6'}
        defaultValues={defaultValues as AdditionalInfoFormData}>

        <NumberField name="apprentiNumber" title={'Effectif d\'apprentis'} placeholder={'1200'} />

        <SelectField name={'nda'}
                     title={'NDA'}
                     options={ndaOptions}
                     disabled={inscription.ndas.length < 2}
                     tooltip={t['DONNEE_OFA_COMP_NDA_INFO_BULLE_MSG']} />

        <div className={'flex justify-end mt-6 gap-6'}>
          <Button
            type="button"
            priority="secondary"
            onClick={() => navigate('/inscription/etape-2')}>PRÉCÉDENT</Button>
          <Button type="submit">VALIDER</Button>
        </div>
      </Form>
    </div>
  );
};
